import { ICONS, TABS } from './TabBar.jsx'
import { MONEY_SECTIONS } from '../routes/Money.jsx'
import { isCashflow } from '../lib/notes.js'
import PaneResizer from './PaneResizer.jsx'

const CATEGORY_COLORS = {
  Cashflow: '#12A150',
  Health: '#FF2D55',
  Personal: '#DD6B00',
  Notes: '#5856D6',
  'My Projects': '#AF52DE',
}

const MAIN_COLORS = {
  today: '#007AFF',
  calendar: '#FF3B30',
  reminders: '#CC5500',
  log: '#8E8E93',
  search: '#00838F',
}

const MONEY_COLORS = {
  cashflow: '#12A150',
  income: '#0A7F47',
  builds: '#0B6EAF',
  plans: '#B45309',
  subs: '#7C3AED',
  networth: '#B4235A',
  reports: '#46515F',
}

const CATEGORY_ICONS = {
  Cashflow: <><rect x="3" y="6" width="18" height="12" rx="2" /><circle cx="12" cy="12" r="2" /><path d="M7 9H6v6h1M17 9h1v6h-1" /></>,
  Health: <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8L12 21l8.8-8.6a5.5 5.5 0 0 0 0-7.8Z" />,
  Personal: <><circle cx="12" cy="8" r="3.5" /><path d="M5.5 20a6.5 6.5 0 0 1 13 0" /></>,
  Notes: <><rect x="4" y="3" width="16" height="18" rx="2" /><path d="M8 8h8M8 12h8M8 16h5" /></>,
  'My Projects': <><path d="M3.5 7.5h6l2-2h9v13h-17z" /><path d="M3.5 10h17" /></>,
}

const MONEY_ICONS = {
  cashflow: CATEGORY_ICONS.Cashflow,
  income: <><path d="M12 3v14M7 12l5 5 5-5" /><path d="M5 21h14" /></>,
  builds: <><path d="m14 4 6 6" /><path d="m17 3 4 4-9 9-4-4z" /><path d="m7 13-4 4 4 4 4-4" /></>,
  plans: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 10h18M7 15h4" /></>,
  subs: <><path d="m17 2 4 4-4 4" /><path d="M3 11V9a3 3 0 0 1 3-3h15" /><path d="m7 22-4-4 4-4" /><path d="M21 13v2a3 3 0 0 1-3 3H3" /></>,
  networth: <><path d="M4 19V5" /><path d="M4 19h16" /><path d="m7 15 4-4 3 2 5-6" /></>,
  reports: <><path d="M4 20V10M10 20V4M16 20v-7M22 20V7" /><path d="M2 20h22" /></>,
}

const FALLBACK_CATEGORY_ICON = <><circle cx="12" cy="12" r="7" /><path d="M9 12h6M12 9v6" /></>

function SidebarItem({ label, selected, onClick, icon, color }) {
  return (
    <button
      type="button"
      className={'sidebar-item' + (selected ? ' selected' : '')}
      aria-current={selected ? 'page' : undefined}
      onClick={onClick}
    >
      <span className="sidebar-icon-tile" style={{ background: color }} aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
             strokeLinecap="round" strokeLinejoin="round">
          {icon || FALLBACK_CATEGORY_ICON}
        </svg>
      </span>
      <span>{label}</span>
    </button>
  )
}

export default function Sidebar({ categories, current, moneySection, onChange, collapsed, width, onResize }) {
  return (
    <aside
      className={'sidebar' + (collapsed ? ' collapsed' : '')}
      style={{ '--sidebar-width': width + 'px' }}
      aria-label="Sidebar"
    >
      <div className="sidebar-scroll">
        <div className="sidebar-top">
          {TABS.map((tab) => (
            <SidebarItem
              key={tab.key}
              label={tab.label}
              icon={ICONS[tab.icon]}
              color={MAIN_COLORS[tab.key]}
              selected={current === tab.key}
              onClick={() => onChange(tab.key)}
            />
          ))}
        </div>

        <SidebarSection title="Categories">
          {categories.filter((category) => !isCashflow(category)).map((category) => (
            <SidebarItem
              key={category.id}
              label={category.name}
              color={category.color || CATEGORY_COLORS[category.name] || 'var(--ink-faint)'}
              icon={CATEGORY_ICONS[category.name]}
              selected={current === category.id && moneySection === 'none'}
              onClick={() => onChange(category.id, 'none')}
            />
          ))}
        </SidebarSection>

        <SidebarSection title="Money">
          {MONEY_SECTIONS.map((section) => (
            <SidebarItem
              key={section.key}
              label={section.label}
              color={MONEY_COLORS[section.key]}
              icon={MONEY_ICONS[section.key]}
              selected={current === 'money' && moneySection === section.key}
              onClick={() => onChange('money', section.key)}
            />
          ))}
        </SidebarSection>
      </div>

      <PaneResizer
        className="sidebar-resizer"
        label="Resize sidebar"
        min={200}
        max={360}
        value={width}
        tabIndex={collapsed ? -1 : 0}
        onPointerDown={onResize}
      />
    </aside>
  )
}

function SidebarSection({ title, children }) {
  return (
    <section className="sidebar-section">
      <h2>{title}</h2>
      {children}
    </section>
  )
}
